import { useState } from 'react';
import type { EmailDraftCardData } from './types';

/**
 * Editable email result card.
 *
 * The card arrives with the recipient pre-filled and the user's rough note in
 * the body. Two actions:
 *  - ✨ Xavior Rewrite — rewrites the rough note into a complete email (subject
 *    + body), ALTERNATING between a professional and a casual tone on each click
 *    and diverging from the previous draft, so clicking again gives a fresh
 *    take. onCompose() does the generation (parent fetch) and returns the draft.
 *  - Send — sends the email. onSend() does the actual POST (parent) and returns
 *    success so the card can lock on send (no double-send of a real email).
 *
 * Each rewrite re-rewrites the ORIGINAL note (`seed`), not the already-polished
 * body, so iterations stay anchored to the user's intent instead of drifting.
 *
 * Mirrors the bordered card chrome of EmailResultCard (rc-card / rc-meta).
 */
export function EmailDraftCard({
  data,
  onSend,
  onCompose,
}: {
  data: EmailDraftCardData;
  onSend: (edited: { to: string; subject: string; body: string }) => void | Promise<boolean | void>;
  onCompose?: (
    args: { style: 'casual' | 'professional'; rewriteFrom: string; avoidBody?: string },
  ) => Promise<{ subject?: string; body?: string } | null | void>;
}) {
  const [to, setTo] = useState(data.to ?? '');
  const [subject, setSubject] = useState(data.subject ?? '');
  const [body, setBody] = useState(data.body ?? '');
  const [sendState, setSendState] = useState<'idle' | 'sending' | 'sent'>('idle');
  // Which tone is currently being generated (drives the per-button "Writing…").
  const [rewritingStyle, setRewritingStyle] = useState<'casual' | 'professional' | null>(null);
  // Original rough note, captured at mount — every rewrite re-rewrites THIS.
  const [seed] = useState(data.body ?? '');
  // Tone of the draft currently shown, surfaced in the meta row.
  const [lastStyle, setLastStyle] = useState<'casual' | 'professional' | null>(null);
  // Rewrite alternates tone on each click (professional → casual → …); no tone
  // picker, just a single "Rewrite" the user can tap again for a different take.
  const [rewriteCount, setRewriteCount] = useState(0);

  const busy = sendState !== 'idle' || rewritingStyle !== null;
  // Reply mode (inbox reply) drafts via /draft-reply and needs no contactId;
  // compose mode (email_compose) needs a linked contact to voice the draft.
  const canRewrite = Boolean(onCompose && (data.contactId || data.replyMode));

  const handleRewrite = async () => {
    if (busy || !onCompose) return;
    // Alternate tone each click: professional, casual, professional, …
    const style: 'casual' | 'professional' = rewriteCount % 2 === 0 ? 'professional' : 'casual';
    setRewritingStyle(style);
    try {
      const draft = await onCompose({
        style,
        rewriteFrom: seed,
        // Diverge from the current draft so re-running gives a fresh take.
        avoidBody: body && body !== seed ? body : undefined,
      });
      if (draft) {
        if (typeof draft.subject === 'string') setSubject(draft.subject);
        if (typeof draft.body === 'string') setBody(draft.body);
        setLastStyle(style);
        // A fresh rewrite supersedes any prior "sent" lock.
        setSendState('idle');
      }
      setRewriteCount((n) => n + 1);
    } catch {
      // Leave fields untouched on failure; the parent surfaces a toast.
    } finally {
      setRewritingStyle(null);
    }
  };

  const handleSend = async () => {
    // Guard against double-send: a real email is dispatched, so a rapid second
    // click (or a click after success) must be a no-op.
    if (busy || !to.trim() || !body.trim()) return;
    setSendState('sending');
    try {
      const ok = await onSend({ to, subject, body });
      setSendState(ok === false ? 'idle' : 'sent');
    } catch {
      setSendState('idle');
    }
  };

  // Once sent, the card FLIPS to a clean confirmation — satisfying, unambiguous
  // proof the email went out (vs. leaving the editable form on screen).
  if (sendState === 'sent') {
    return (
      <div className="rc-card rc-email-sent">
        <div className="rc-meta">
          <span className="rc-meta-kind rc-sent-kind">
            <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5" /></svg>
            SENT
          </span>
          <span className="rc-meta-sep">·</span>
          <span className="rc-meta-match">TO {(data.toName || to).toUpperCase()}</span>
        </div>
        {subject && <div className="rc-sent-subject">{subject}</div>}
        {body && <div className="rc-sent-snippet">{body}</div>}
        <div className="rc-sent-foot">Delivered to {to}</div>
      </div>
    );
  }

  return (
    <div className="rc-card rc-email-draft">
      <div className="rc-meta">
        <span className="rc-meta-kind">{data.replyMode ? '↩️ REPLY' : '✉️ EMAIL'}</span>
        {data.toName && (
          <>
            <span className="rc-meta-sep">·</span>
            <span className="rc-meta-match">TO {data.toName.toUpperCase()}</span>
          </>
        )}
        {lastStyle && (
          <>
            <span className="rc-meta-sep">·</span>
            <span className="rc-meta-match">{lastStyle.toUpperCase()}</span>
          </>
        )}
      </div>

      <label className="rc-field">
        <span className="rc-field-label">To</span>
        <input
          className="rc-input"
          type="text"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          placeholder="recipient@example.com"
        />
      </label>

      <label className="rc-field">
        <span className="rc-field-label">Subject</span>
        <input
          className="rc-input"
          type="text"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="Subject"
        />
      </label>

      <label className="rc-field">
        <span className="rc-field-label">Body</span>
        <textarea
          className="rc-textarea"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={5}
          placeholder={
            data.replyMode
              ? 'Hit Rewrite, or type your reply…'
              : canRewrite
                ? 'Jot a rough note, then hit Rewrite…'
                : 'Write your message…'
          }
        />
      </label>

      <div className="rc-actions rc-actions--draft">
        {canRewrite && (
          <button
            type="button"
            className="rc-btn-draft"
            onClick={handleRewrite}
            disabled={busy}
          >
            {rewritingStyle ? 'Writing…' : 'Rewrite'}
          </button>
        )}
        <button
          type="button"
          className="rc-btn-draft"
          onClick={handleSend}
          disabled={busy || !to.trim() || !body.trim()}
        >
          {/* sendState 'sent' early-returns the confirmation view above, so only
              'sending' / 'idle' reach this button. */}
          {sendState === 'sending' ? 'Sending…' : 'Send'}
        </button>
      </div>
    </div>
  );
}
